import React, { useEffect, useMemo, useState } from 'react';
import { platformCoreNetworkApi, PlatformMember, TalentPasswordManagementResult, TalentTemporaryPasswordResult } from '../../../api/platform/coreNetwork';
import { isApiError } from '../../../api/errors';
import { formatDateTime, formatEmptyValue } from '../utils/format';
import { FeedbackBanner, KeyValueGrid, MvpCard } from './MvpTalentUi';

interface TalentPasswordManagementCardProps {
  member: PlatformMember;
  canManageCredentials: boolean;
  onRefresh: () => Promise<void> | void;
  onFeedback: (feedback: { tone: 'success' | 'error'; message: string }) => void;
}

type PasswordDialog = 'reset' | 'generate' | 'manual' | null;

const PASSWORD_RULES = [
  'At least 8 characters',
  'At least one uppercase letter',
  'At least one lowercase letter',
  'At least one number',
  'At least one special character',
];

const hasStrongPasswordShape = (value: string) => (
  value.length >= 8
  && /[A-Z]/.test(value)
  && /[a-z]/.test(value)
  && /\d/.test(value)
  && /[^A-Za-z0-9]/.test(value)
);

const safePasswordErrorMessage = (error: unknown) => {
  if (isApiError(error)) {
    if (error.kind === 'forbidden') return 'You do not have permission to manage Talent Portal passwords.';
    if (error.statusCode === 429) return 'Too many password-management attempts. Wait before trying again.';
    return error.message || 'Unable to manage this Talent Portal password.';
  }
  return error instanceof Error ? error.message : 'Unable to manage this Talent Portal password.';
};

const credentialStatusItems = (member: PlatformMember) => [
  { label: 'Portal status', value: formatEmptyValue(member.portalStatus) },
  { label: 'Last login', value: formatDateTime(member.portalLastLoginAt) },
  { label: 'Last password reset', value: formatDateTime(member.portalLastPasswordResetAt) },
  { label: 'Temporary password expiry', value: formatDateTime(member.portalTemporaryPasswordExpiresAt) },
  { label: 'Password change required', value: member.portalPasswordChangeRequired === undefined ? 'Not provided' : member.portalPasswordChangeRequired ? 'Yes' : 'No' },
  { label: 'Locked until', value: formatDateTime(member.portalLockedUntil) },
  { label: 'Last reset administrator', value: formatEmptyValue(member.portalLastPasswordResetByUserId ? `User #${member.portalLastPasswordResetByUserId}` : null) },
];

const TalentPasswordManagementCard: React.FC<TalentPasswordManagementCardProps> = ({ member, canManageCredentials, onRefresh, onFeedback }) => {
  const [dialog, setDialog] = useState<PasswordDialog>(null);
  const [reason, setReason] = useState('');
  const [bccAdministrator, setBccAdministrator] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [requirePasswordChange, setRequirePasswordChange] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TalentTemporaryPasswordResult | null>(null);
  const [copied, setCopied] = useState(false);

  const manualPasswordError = useMemo(() => {
    if (dialog !== 'manual') return null;
    if (password && !hasStrongPasswordShape(password)) return 'Password does not meet the Talent Portal password rules.';
    if (confirmPassword && password !== confirmPassword) return 'Password and confirmation must match.';
    return null;
  }, [confirmPassword, dialog, password]);

  useEffect(() => {
    if (!dialog) {
      setReason('');
      setBccAdministrator(false);
      setSendEmail(true);
      setRequirePasswordChange(true);
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setError(null);
      setCopied(false);
    }
  }, [dialog]);

  if (!canManageCredentials) return null;

  const closeResultDialog = () => {
    setResult(null);
    setCopied(false);
  };

  const finishSuccess = async (message: string, response?: TalentPasswordManagementResult) => {
    const extra = typeof response?.sessionsRevoked === 'number' ? ` ${response.sessionsRevoked} session${response.sessionsRevoked === 1 ? '' : 's'} revoked.` : '';
    onFeedback({ tone: 'success', message: `${message}${extra}` });
    await onRefresh();
  };

  const submitReset = async () => {
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await platformCoreNetworkApi.sendTalentPasswordReset(member.id, { reason: reason.trim(), sendEmail: true, bccAdministrator });
      setDialog(null);
      await finishSuccess('Secure password reset sent.', response);
    } catch (submitError) {
      setError(safePasswordErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  const submitGenerate = async () => {
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await platformCoreNetworkApi.generateTalentTemporaryPassword(member.id, { reason: reason.trim(), sendEmail, bccAdministrator });
      setDialog(null);
      setResult(response);
      await finishSuccess('Temporary password generated.', response);
    } catch (submitError) {
      setError(safePasswordErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  const submitManual = async () => {
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    if (!password || !confirmPassword) {
      setError('New temporary password and confirmation are required.');
      return;
    }
    if (manualPasswordError) {
      setError(manualPasswordError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await platformCoreNetworkApi.setTalentTemporaryPassword(member.id, {
        password,
        confirmPassword,
        reason: reason.trim(),
        sendEmail,
        bccAdministrator,
        requirePasswordChange,
      });
      setPassword('');
      setConfirmPassword('');
      setDialog(null);
      await finishSuccess('Temporary password set by administrator.', response);
    } catch (submitError) {
      setPassword('');
      setConfirmPassword('');
      setError(safePasswordErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  const renderDialog = () => {
    if (!dialog) return null;
    const isReset = dialog === 'reset';
    const isGenerate = dialog === 'generate';
    const title = isReset ? 'Send secure password reset' : isGenerate ? 'Generate temporary password' : 'Set temporary password manually';
    const submit = isReset ? submitReset : isGenerate ? submitGenerate : submitManual;
    return (
      <div className="mvp-dialog-backdrop" role="presentation" onClick={() => !submitting && setDialog(null)}>
        <div className="mvp-dialog mvp-dialog--wide" role="dialog" aria-modal="true" aria-labelledby="talent-password-dialog-title" onClick={(event) => event.stopPropagation()}>
          <div className="mvp-dialog__header">
            <div>
              <h3 id="talent-password-dialog-title">{title}</h3>
              <p>{isReset ? 'This sends the talent member a secure, single-use password-reset link. It does not reveal or change their password inside Command Centre.' : 'This will replace the talent member’s current password, revoke existing Talent Portal sessions, and require the talent member to change the temporary password at next login.'}</p>
            </div>
            <button type="button" className="mvp-button mvp-button--ghost" aria-label="Close password management dialog" onClick={() => setDialog(null)} disabled={submitting}>×</button>
          </div>
          {error ? <FeedbackBanner tone="error" message={error} /> : null}
          <label className="mvp-field">
            <span>Reason *</span>
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} disabled={submitting} aria-describedby="password-reason-help" />
          </label>
          <div id="password-reason-help" className="mvp-field-help">This reason is sent to Elite Core for the platform audit history.</div>
          {!isReset ? (
            <div className="mvp-warning-panel" id="temporary-password-warning">High-risk action: existing Talent Portal sessions may be revoked and the talent must change the temporary password after signing in.</div>
          ) : null}
          {dialog === 'manual' ? (
            <>
              <div className="mvp-password-policy" aria-label="Talent Portal password rules">
                <strong>Talent Portal password rules</strong>
                <ul>{PASSWORD_RULES.map((rule) => <li key={rule}>{rule}</li>)}</ul>
              </div>
              <label className="mvp-field">
                <span>New temporary password *</span>
                <div className="mvp-password-input-row">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" disabled={submitting} aria-describedby="manual-password-error" />
                  <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setShowPassword((value) => !value)} disabled={submitting} aria-label={showPassword ? 'Hide temporary password' : 'Show temporary password'}>{showPassword ? 'Hide' : 'Show'}</button>
                </div>
              </label>
              <label className="mvp-field">
                <span>Confirm temporary password *</span>
                <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" disabled={submitting} aria-describedby="manual-password-error" />
              </label>
              {manualPasswordError ? <div id="manual-password-error" className="mvp-field-error">{manualPasswordError}</div> : null}
              <label className="mvp-checkbox"><input type="checkbox" checked={requirePasswordChange} onChange={(event) => setRequirePasswordChange(event.target.checked)} disabled={submitting} /><span>Require password change at next login</span></label>
            </>
          ) : null}
          {!isReset ? <label className="mvp-checkbox"><input type="checkbox" checked={sendEmail} onChange={(event) => setSendEmail(event.target.checked)} disabled={submitting} /><span>Email the talent member that their password was reset</span></label> : null}
          <label className="mvp-checkbox"><input type="checkbox" checked={bccAdministrator} onChange={(event) => setBccAdministrator(event.target.checked)} disabled={submitting} /><span>Send BCC confirmation to configured administrator address</span></label>
          <div className="mvp-dialog__actions">
            <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialog(null)} disabled={submitting}>Cancel</button>
            <button type="button" className={`mvp-button ${isGenerate || dialog === 'manual' ? 'mvp-button--danger' : ''}`.trim()} onClick={submit} disabled={submitting || !reason.trim() || (dialog === 'manual' && (!password || !confirmPassword || Boolean(manualPasswordError)))} aria-describedby={!isReset ? 'temporary-password-warning' : undefined}>{submitting ? 'Submitting...' : isReset ? 'Send reset email' : isGenerate ? 'Generate temporary password' : 'Set temporary password'}</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MvpCard title="Password Management">
      <div className="mvp-password-management">
        <p>Send a secure password-reset link or issue a temporary password.</p>
        <KeyValueGrid items={credentialStatusItems(member)} />
        <div className="mvp-password-management__primary">
          <button type="button" className="mvp-button" onClick={() => setDialog('reset')}>Send secure password reset</button>
        </div>
        <div className="mvp-password-management__advanced">
          <h4>Advanced password actions</h4>
          <p>Temporary passwords are operationally sensitive and should be used only when a secure reset link is not sufficient.</p>
          <div className="mvp-inline-actions">
            <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialog('generate')}>Generate temporary password</button>
            <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialog('manual')}>Set temporary password manually</button>
          </div>
        </div>
      </div>
      {renderDialog()}
      {result?.temporaryPassword ? (
        <div className="mvp-dialog-backdrop" role="presentation">
          <div className="mvp-dialog" role="dialog" aria-modal="true" aria-labelledby="temporary-password-result-title">
            <h3 id="temporary-password-result-title">Temporary password</h3>
            <p>This password cannot be retrieved after this dialog is closed.</p>
            <label className="mvp-field">
              <span>Temporary password</span>
              <div className="mvp-password-result-row">
                <code>{result.temporaryPassword}</code>
                <button type="button" className="mvp-button mvp-button--ghost" onClick={async () => { await navigator.clipboard?.writeText(result.temporaryPassword || ''); setCopied(true); }}>{copied ? 'Copied' : 'Copy'}</button>
              </div>
            </label>
            <KeyValueGrid items={[{ label: 'Expires', value: formatDateTime(result.expiresAt) }, { label: 'Password change required', value: result.mustChangePassword ? 'Yes' : 'No' }, { label: 'Sessions revoked', value: typeof result.sessionsRevoked === 'number' ? result.sessionsRevoked : 'Not provided' }]} />
            <p>The talent member must change this password after signing in.</p>
            <div className="mvp-dialog__actions"><button type="button" className="mvp-button" onClick={closeResultDialog}>I have copied it</button></div>
          </div>
        </div>
      ) : null}
    </MvpCard>
  );
};

export default TalentPasswordManagementCard;
